/**
 * Applications Tab (Tab 3)
 * 
 * Financial applications inventory management
 * Features:
 * - Table view with all applications
 * - Add/Edit/Delete via side panel
 * - Risk ranking color coding
 * - Linked processes and controls display
 * - API integration
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Dropdown, Tooltip, message, Spin } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import { MoreOutlined, EditOutlined, DeleteOutlined, LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import ExportPanel from '../../../../components/common/ExportPanel';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import { setApplications } from '../../../../store/slices/auditPlanSlice';
import ArrowUpIcon from '../../../../assets/icons/ic-arrow-up.svg?react';
import ArrowDownIcon from '../../../../assets/icons/ic-arrow-down.svg?react';
import AddItemButton from '../../../../components/common/AddItemButton';
import ConfirmModal from '../../../../components/common/ConfirmModal';
import trashIcon from '../../../../assets/icons/ic-trash.svg';
import ApplicationItemDrawer, { 
  type ApplicationFormData, 
} from './ApplicationItemDrawer';
import {
  getApplications,
  createApplication,
  updateApplication,
  deleteApplication as deleteApplicationApi,
} from '../../../../services/auditPlanService';
import { RiskRanking, mapRiskRankingToApi } from '../../../../services/types';
import { setControlActivities } from '../../../../store/slices/controlActivitiesSlice';
import { getControlActivities } from '../../../../services/controlActivitiesService';

// ========== TYPE DEFINITIONS ==========

export interface Application {
  id: string;
  index: number;
  name: string;
  auditScope: 'In Scope' | 'Out of Scope';
  riskRanking: RiskRanking;
  systemAdministrator: string;
  description: string;
  auditorNotes?: string;
  processes: Array<{ id: string; name: string }>;
  controlActivities: Array<{ id: string; name: string }>;
  linkedControlsCount: number;
  createdAt: string;
}

// ========== STYLED COMPONENTS ==========

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const TableWrapper = styled.div`
  background: #E0E7FF;
  border-radius: 6px;
  border: 1px solid #E0E7FF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  padding: 2px;

  .ant-table {
    border-radius: 4px;
    overflow: hidden;
    table-layout: fixed;
  }

  .ant-table-thead > tr > th {
    background: #F8FAFC;
    font-family: Inter;
    font-weight: 500;
    font-size: 12px;
    color: #6B7280;
    padding: 12px 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    
    &:first-child {
      border-left: none;
    }
    
    /* Remove Ant Design's internal column separators */
    &::before {
      display: none !important;
    }
  }

  .ant-table-tbody > tr > td {
    font-family: Inter;
    font-size: 14px;
    color: #1F2937;
    padding: 16px;
    border-bottom: 1px solid #E5E7EB;
    border-left: 1px solid #E5E7EB;
    overflow: hidden;
    
    &:first-child {
      border-left: none;
    }
  }

  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }

  .ant-table-tbody > tr:hover > td {
    background: #F9FAFB;
  }
`;

const IndexCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 0 12px;
  border: 1px solid #E5E7EB;
  border-radius: 14px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  color: #6B7280;
`;

const NameChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 20px;
  font-family: Inter;
  font-weight: 500;
  font-size: 13px;
  color: #1E40AF;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #DBEAFE;
  }
`;

const ScopeChip = styled.span<{ $inScope: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-family: Inter;
  font-weight: 500;
  font-size: 12px;
  
  ${({ $inScope }) => $inScope ? `
    background: #ECFDF5;
    color: #065F46;
  ` : `
    background: #F3F4F6;
    color: #6B7280;
  `}
`;

const RiskChip = styled.span<{ $risk: string }>`
  display: inline-block;
  padding: 8px 16px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 500;
  cursor: default;
  background: white;

  ${({ $risk }) => {
    switch ($risk) {
      case 'High':
        return `
          border: 1px solid #EF4444;
          color: #EF4444;
        `;
      case 'Med':
        return `
          border: 1px solid #FACC15;
          color: #FACC15;
        `;
      case 'Low':
        return `
          border: 1px solid #2DD4BF;
          color: #2DD4BF;
        `;
      default:
        return `
          border: 1px solid #60A5FA;
          color: #60A5FA;
        `;
    }
  }}

  &::before {
    content: '●';
    margin-right: 6px;
    font-size: 10px;
  }
`;

// Process Tag style (from FinancialStatementsPage.tsx)
const ProcessTag = styled.span`
  display: inline-block;
  padding: 4px 12px;
  margin: 2px 4px 2px 0;
  background: #EFF6FF;
  color: #1E40AF;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
    transform: translateY(-1px);
  }
`;


// Tooltip content styling (from FinancialStatementsPage.tsx)
const TooltipTitle = styled.div`
  font-family: Inter;
  font-weight: 600;
  font-size: 16px;
  line-height: 24px;
  color: #1F2937;
  text-align: center;
  margin-bottom: 12px;
`;

const TooltipList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TooltipItem = styled.div`
  padding: 8px 12px;
  background: #EFF6FF;
  border-radius: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #2563EB;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  white-space: normal;
  word-break: break-word;

  &:hover {
    background: #DBEAFE;
    transform: translateX(2px);
  }
`;

const ActionButton = styled.button`
  background: #F8FAFC;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  padding: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #6B7280;
  transition: all 0.3s;

  &:hover {
    color: #1F2937;
    background: #EFF6FF;
  }

  .anticon {
    font-size: 20px;
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

const MenuItemText = styled.span`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #1F2937;
`;

const MenuItemTextDanger = styled(MenuItemText)`
  color: #DC2626;
`;

const EmptyStateWrapper = styled.div`
  text-align: center;
  padding: 80px 20px;
  background: white;
  border-radius: 6px;
`;

const EmptyStateText = styled.div`
  color: #1F2937;
  font-family: Inter;
  font-size: 15px;
  font-weight: 500;
  margin-bottom: 24px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 200px;
  background: white;
  border-radius: 6px;
`;

// ========== PROPS ==========

interface ApplicationsTabProps {
  /** Trigger to open export panel from parent */
  triggerExport?: boolean;
  /** Callback when export panel is closed */
  onExportClose?: () => void;
}

// ========== COMPONENT ==========

const ApplicationsTab: React.FC<ApplicationsTabProps> = ({
  triggerExport,
  onExportClose,
}) => {
  // Hooks
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isAdmin } = useSelector((state: RootState) => state.auth);
  // Redux state
  const { selectedReport } = useSelector((state: RootState) => state.annualReport);
  const auditPlanState = useSelector((state: RootState) => state.auditPlan);
  const { controlActivities: allControlActivities } = useSelector((state: RootState) => state.controlActivities);
  
  // Get applications from Redux, resolve empty control names from controlActivities store
  const applications = useMemo(() => {
    const rawApps = auditPlanState?.applications || [];
    if (allControlActivities.length === 0) return rawApps;

    const controlMap = new Map(allControlActivities.map(c => [c.id, c.controlCode]));
    return rawApps.map(app => {
      const needsResolve = app.controlActivities.some(c => c.id && !c.name);
      if (!needsResolve) return app;
      return {
        ...app,
        controlActivities: app.controlActivities.map(c => ({
          id: c.id,
          name: c.name || controlMap.get(c.id) || c.id,
        })),
      };
    });
  }, [auditPlanState?.applications, allControlActivities]);
  
  // Local UI state
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'add' | 'edit'>('add');
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [insertAtIndex, setInsertAtIndex] = useState<number | null>(null);
  
  // Delete confirmation
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<Application | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Export panel state
  const [isExportPanelOpen, setIsExportPanelOpen] = useState(false);

  const { controlActivities, lastFetchedReportId } = useSelector(
    (state: RootState) => state.controlActivities
  );
  

  // Watch for triggerExport from parent
  useEffect(() => {
    if (triggerExport) {
      setIsExportPanelOpen(true);
    }
  }, [triggerExport]);

   useEffect(() => {
      const fetchControlActivitiesIfNeeded = async () => {
        if (!open) return;
        if (!selectedReport?.annualReportId) return;
        
        // If control activities are already loaded for this report, skip fetching
        if (controlActivities.length > 0 && lastFetchedReportId === selectedReport.annualReportId) {
          // console.log('✅ Control activities already in Redux, skipping fetch');
          return;
        }

        try {
          // console.log('🔄 Fetching control activities for application drawer...');
          const response = await getControlActivities({
            annualReportId: selectedReport.annualReportId,
            sorted: [{name: "ControlCode", direction: "ascending"}],
          });
  
          dispatch(setControlActivities({
            data: response.data,
            totalCount: response.totalCount,
            reportId: selectedReport.annualReportId,
          }));
          // console.log(`✅ Control activities loaded: ${response.data.length} items`);
        } catch (error) {
          console.error('❌ Failed to fetch control activities:', error);
        } finally {
          
        }
      };
  
      fetchControlActivitiesIfNeeded();
    }, [open, selectedReport?.annualReportId, controlActivities.length, lastFetchedReportId, dispatch]);

  // Fetch applications
  const fetchApplications = useCallback(async () => {
    if (!selectedReport?.annualReportId) {
      return;
    }

    setLoading(true);
    try {
      const response = await getApplications({
        annualReportId: selectedReport.annualReportId,
      });
      
      // Dispatch to Redux - data is already mapped and sorted by Index in the service
      dispatch(setApplications({
        data: response.data,
        totalCount: response.totalCount,
        reportId: selectedReport.annualReportId,
      }));
    } catch (error: any) {
      console.error('Failed to fetch applications:', error);
      message.error(error?.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [selectedReport?.annualReportId, dispatch]);

  // Fetch applications when selectedReport changes
  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  // Open drawer for adding new application
  const openAddDrawer = useCallback((atIndex?: number) => {
    setDrawerMode('add');
    setEditingApp(null);
    setInsertAtIndex(atIndex ?? null);
    setDrawerOpen(true);
  }, []);

  // Open drawer for editing
  const openEditDrawer = useCallback((app: Application) => {
    setDrawerMode('edit');
    setEditingApp(app);
    setInsertAtIndex(null);
    setDrawerOpen(true);
  }, []);

  // Handle add above
  const handleAddAbove = useCallback((app: Application) => {
    const index = applications.findIndex(a => a.id === app.id);
    openAddDrawer(index);
  }, [applications, openAddDrawer]);

  // Handle add below
  const handleAddBelow = useCallback((app: Application) => {
    const index = applications.findIndex(a => a.id === app.id);
    openAddDrawer(index + 1);
  }, [applications, openAddDrawer]);

  // Handle drawer submit
  const handleDrawerSubmit = useCallback(async (formData: ApplicationFormData) => {
    if (!selectedReport?.annualReportId) {
      message.error('No annual report selected');
      return;
    }

    try {
      if (drawerMode === 'add') {
        // Calculate the index for the new application
        let newIndex: number;
        if (insertAtIndex !== null) {
          newIndex = insertAtIndex;
        } else {
          newIndex = applications.length;
        }

        await createApplication({
          annualReportId: selectedReport.annualReportId,
          index: newIndex,
          name: formData.name,
          isInScope: formData.auditScope === 'In Scope',
          riskRating: mapRiskRankingToApi(formData.riskRanking),
          systemAdmin: formData.systemAdministrator,
          description: formData.description,
          controlIds: formData.controlIds,
        });

        message.success('Application created successfully');
      } else if (drawerMode === 'edit' && editingApp) {
        await updateApplication({
          annualReportId: selectedReport.annualReportId,
          auditApplicationId: editingApp.id,
          index: editingApp.index,
          name: formData.name,
          isInScope: formData.auditScope === 'In Scope',
          riskRating: mapRiskRankingToApi(formData.riskRanking),
          systemAdmin: formData.systemAdministrator,
          description: formData.description,
          controlIds: formData.controlIds,
        });

        message.success('Application updated successfully');
      }

      // Re-fetch to update Redux
      await fetchApplications();

      setDrawerOpen(false);
      setEditingApp(null);
      setInsertAtIndex(null);
    } catch (error: any) {
      console.error('Failed to save application:', error);
      message.error(error?.message || 'Failed to save application');
    }
  }, [drawerMode, editingApp, insertAtIndex, selectedReport?.annualReportId, applications.length, fetchApplications]);

  // Open delete modal
  const openDeleteModal = useCallback((app: Application) => {
    setAppToDelete(app);
    setDeleteModalOpen(true);
  }, []);

  // Handle delete confirm
  const handleDeleteConfirm = useCallback(async () => {
    if (!appToDelete) return;

    setDeleting(true);
    try {
      await deleteApplicationApi(appToDelete.id);
      
      message.success('Application deleted successfully');
      
      // Re-fetch to update Redux
      await fetchApplications();
      
      setDeleteModalOpen(false);
      setAppToDelete(null);
    } catch (error: any) {
      console.error('Failed to delete application:', error);
      message.error(error?.message || 'Failed to delete application');
    } finally {
      setDeleting(false);
    }
  }, [appToDelete, fetchApplications]);

  // Get menu items for row actions
  const getMenuItems = (app: Application): MenuProps['items'] => [
    {
      key: 'add-above',
      label: (
        <MenuItemContent>
          <ArrowUpIcon />
          <MenuItemText>Add Above</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => handleAddAbove(app),
    },
    {
      key: 'add-below',
      label: (
        <MenuItemContent>
          <ArrowDownIcon />
          <MenuItemText>Add Below</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => handleAddBelow(app),
    },
    {
      key: 'edit',
      label: (
        <MenuItemContent>
          <EditOutlined />
          <MenuItemText>Edit</MenuItemText>
        </MenuItemContent>
      ),
      onClick: () => openEditDrawer(app),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      label: (
        <MenuItemContent>
          <DeleteOutlined style={{ color: '#DC2626' }} />
          <MenuItemTextDanger>Delete</MenuItemTextDanger>
        </MenuItemContent>
      ),
      onClick: () => openDeleteModal(app),
    },
  ];

  // Handle navigation to Process Hierarchy (EX3)
  const handleProcessClick = useCallback((processId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/process-hierarchy?processId=${processId}`);
  }, [navigate]);

  // Handle navigation to Control Activities (EX5)
  const handleControlClick = useCallback((controlId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/control-activities/${controlId}`);
  }, [navigate]);

  // Render linked items with tooltip - show all items on single line, cutoff by cell border
  const renderLinkedItems = (
    items: Array<{ id: string; name: string }>, 
    title: string,
    type: 'process' | 'control'
  ) => {
    if (items.length === 0) {
      return null;
    }

    const handleClick = type === 'process' ? handleProcessClick : handleControlClick;

    // Tooltip content for multiple items (styled like FinancialStatementsPage.tsx)
    const tooltipContent = items.length > 0 ? (
      <div>
        <TooltipTitle>{title}</TooltipTitle>
        <TooltipList>
          {items.map((item) => (
            <TooltipItem 
              key={item.id}
              onClick={(e) => handleClick(item.id, e)}
            >
              {item.name}
            </TooltipItem>
          ))}
        </TooltipList>
      </div>
    ) : null;

    const content = (
      <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '4px', overflow: 'hidden', maxHeight: '28px', alignItems: 'center' }}>
        {items.map((item) => (
          <ProcessTag 
            key={item.id}
            onClick={(e) => handleClick(item.id, e)}
          >
            {item.name}
          </ProcessTag>
        ))}
      </div>
    );

    if (tooltipContent) {
      return (
        <Tooltip
          title={tooltipContent}
          placement="top"
          overlayStyle={{ maxWidth: '400px' }}
          overlayInnerStyle={{
            backgroundColor: '#FFFFFF',
            padding: '16px',
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.15)',
          }}
          color="#FFFFFF"
        >
          {content}
        </Tooltip>
      );
    }

    return content;
  };

  // Table columns
  const columns: TableColumnsType<Application> = [
    {
      title: '#',
      key: 'index',
      width: 68,
      align: 'center',
      render: (_: unknown, __: Application, index: number) => (
        <IndexCell>{(index + 1).toString().padStart(2, '0')}</IndexCell>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 180,
      render: (name: string, record: Application) => (
        <NameChip onClick={() => openEditDrawer(record)}>
          {name}
        </NameChip>
      ),
    },
    {
      title: 'Audit scope',
      dataIndex: 'auditScope',
      key: 'auditScope',
      width: 120,
      render: (scope: string) => (
        <ScopeChip $inScope={scope === 'In Scope'}>
          {scope}
        </ScopeChip>
      ),
    },
    {
      title: 'Risk ranking',
      dataIndex: 'riskRanking',
      key: 'riskRanking',
      width: 130,
      render: (risk: string) => (
        <RiskChip $risk={risk}>
          {risk}
        </RiskChip>
      ),
    },
    {
      title: 'System admin',
      dataIndex: 'systemAdministrator',
      key: 'systemAdministrator',
      width: 150,
    },
    {
      title: 'System description',
      dataIndex: 'description',
      key: 'description',
    },
    // {
    //   title: 'Processes',
    //   dataIndex: 'processes',
    //   key: 'processes',
    //   width: 200,
    //   ellipsis: true,
    //   onCell: () => ({
    //     style: { maxWidth: 200, overflow: 'hidden' }
    //   }),
    //   render: (processes: Array<{ id: string; name: string }>) => 
    //     renderLinkedItems(processes, 'Processes', 'process'),
    // },
    {
      title: 'Control activities',
      dataIndex: 'controlActivities',
      key: 'controlActivities',
      width: 220,
      ellipsis: true,
      onCell: () => ({
        style: { maxWidth: 220, overflow: 'hidden' }
      }),
      render: (activities: Array<{ id: string; name: string }>) => 
        renderLinkedItems(activities, 'Control Activities', 'control'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_: unknown, record: Application) => (
        <Dropdown
          menu={{ items: getMenuItems(record) }}
          trigger={['click']}
          placement="bottomRight"
        >
          <ActionButton disabled={!isAdmin} onClick={(e) => e.stopPropagation()}>
            <MoreOutlined />
          </ActionButton>
        </Dropdown>
      ),
    },
  ];

  // Convert Application to ApplicationFormData
  const getFormData = (app: Application | null): ApplicationFormData | null => {
    if (!app) return null;
    return {
      id: app.id,
      name: app.name,
      auditScope: app.auditScope,
      riskRanking: app.riskRanking,
      systemAdministrator: app.systemAdministrator,
      description: app.description,
      controlIds: app.controlActivities.map(c => c.id),
    };
  };
  
  // Get existing application names for duplicate validation
  const existingNames = applications.map(app => app.name);

  // Export handler
  const handleCloseExportPanel = useCallback(() => {
    setIsExportPanelOpen(false);
    onExportClose?.();
  }, [onExportClose]);

  // Prepare export data
  const exportData = useMemo(() => {
    return applications.map((app, index) => ({
      index: index + 1,
      name: app.name,
      auditScope: app.auditScope,
      riskRanking: app.riskRanking,
      systemAdministrator: app.systemAdministrator || '-',
      description: app.description || '-',
      processes: app.processes.map(p => p.name).join(', ') || '-',
      controlActivities: app.controlActivities.map(c => c.name).join(', ') || '-',
    }));
  }, [applications]);

  return (
    <Container>
      {/* Table */}
      {loading ? (
        <LoadingWrapper>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
        </LoadingWrapper>
      ) : applications.length > 0 ? (
        <TableWrapper>
          <Table
            dataSource={applications}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="middle"
          />
        </TableWrapper>
      ) : (
        <EmptyStateWrapper>
          <EmptyStateText>There are no applications to display yet.</EmptyStateText>
          <AddItemButton
            text="Add Application"
            onClick={() => openAddDrawer()}
            disabled={!isAdmin}
          />
        </EmptyStateWrapper>
      )}

      {/* Application Drawer */}
      <ApplicationItemDrawer
        open={drawerOpen}
        mode={drawerMode}
        initialValues={getFormData(editingApp)}
        existingNames={existingNames}
        onClose={() => {
          setDrawerOpen(false);
          setEditingApp(null);
          setInsertAtIndex(null);
        }}
        onSubmit={handleDrawerSubmit}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setAppToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        icon={trashIcon}
        title="Delete Application"
        description={
          <>
            Are you sure you want to delete <strong>{appToDelete?.name}</strong>?
            {appToDelete && appToDelete.linkedControlsCount > 0 && (
              <>
                <br /><br />
                This application is linked to {appToDelete.linkedControlsCount} control(s). 
                The application will be automatically unlinked from those controls.
              </>
            )}
            <br /><br />
            This action cannot be undone.
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        danger={true}
        loading={deleting}
      />

      {/* Export Panel */}
      <ExportPanel
        open={isExportPanelOpen}
        onClose={handleCloseExportPanel}
        data={exportData}
        columns={[
          { key: 'index', title: '#' },
          { key: 'name', title: 'Name' },
          { key: 'auditScope', title: 'Audit Scope' },
          { key: 'riskRanking', title: 'Risk Ranking' },
          { key: 'systemAdministrator', title: 'System Admin' },
          { key: 'description', title: 'System Description' },
          { key: 'processes', title: 'Processes' },
          { key: 'controlActivities', title: 'Control Activities' },
        ]}
        fileName="Applications_Report"
        title="Financial Applications Report"
      />
    </Container>
  );
};

export default ApplicationsTab;

